import { useQuery } from '@tanstack/react-query';
import TerritoryService from '~/modules/services/territory-service';

export function useTerritories({ short = false, labelValue = false, params = {}, ...queryOptions } = {}) {
	// Determine the query key based on parameters
	const queryKey = short
		? ['territories', 'short', labelValue, JSON.stringify(params)]
		: ['territories', 'full', JSON.stringify(params)];

	const {
		data = [],
		isLoading,
		isError,
		error,
		dataUpdatedAt: lastLoadedAt,
		refetch
	} = useQuery({
		queryKey,
		queryFn: () => {
			if(short) {
				return labelValue
					? TerritoryService.getAllTerritoriesShortLabelValue(params)
					: TerritoryService.getAllTerritoriesShort(params);
			}
			return TerritoryService.getAllTerritories(params);
		},
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
		...queryOptions // Spread any additional options
	});

	// Transform the data to match the expected format
	const territories = Array.isArray(data.nodes) ? data.nodes : Array.isArray(data) ? data : [];
	const totalCount = data.totalCount || territories.length;

	// Helper for getting territory by ID, code or name
	const getTerritoryById = (id) => {
		return territories.find((territory) => territory.id === id || territory._id === id);
	};

	const getTerritoryByCode = (code) => {
		return territories.find((territory) => territory.code === code);
	};

	// Filter functions from the service
	const getSelectableTerritories = () => {
		return TerritoryService.getSelectableTerritories(territories);
	};

	const getTerritoriesWithCodes = () => {
		return TerritoryService.getTerritoriesWithCodes(territories);
	};

	return {
		territories,
		totalCount,
		isLoading,
		isError,
		error,
		isLoaded: !!territories.length,
		lastLoadedAt: lastLoadedAt ? new Date(lastLoadedAt) : null,
		getTerritoryById,
		getTerritoryByCode,
		getSelectableTerritories,
		getTerritoriesWithCodes,
		refetch
	};
}